/* eslint-disable no-unused-vars */
import {
  getTestAttributes,
  noop,
  sizing,
  icons,
  ContentEditableInput,
  colors,
  useWindowSize,
} from '@chillibean/component-library';
import baselineCancel from '@iconify-icons/ic/baseline-cancel';
import { Icon } from '@iconify/react';
import { clsx } from 'clsx';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

function OtherOptionBox(props) {
  const { handleOtherOptionClick = noop, handleOtherFieldLabel = noop, otherFieldLabel = 'Other' } = props;
  const DEFAULT_OTHER_FIELD_LABEL = 'Other';
  const [originalFieldLabel, _] = useState(otherFieldLabel);
  const [otherFieldLabelEditable, setOtherFieldLabelEditable] = useState(false);
  const [labelValue, setLabelValue] = useState(otherFieldLabel);
  const [detailsWidth, setDetailsWidth] = useState(200);

  const BASE_CONTAINER_PADDING = 20;
  const BASE_WRAPPER_PADDING = 32;
  const BASE_ICONS_WIDTH_WITH_PADDING = 64;
  const EXTRA_GAPS = 16;

  const otherOptionBoxContainerRef = useRef(null);

  const resizeListener = () => {
    if (!otherOptionBoxContainerRef.current) return setDetailsWidth(0);

    return setDetailsWidth(
      otherOptionBoxContainerRef.current.offsetWidth -
        BASE_CONTAINER_PADDING -
        BASE_WRAPPER_PADDING -
        BASE_ICONS_WIDTH_WITH_PADDING -
        EXTRA_GAPS,
    );
  };

  useEffect(() => {
    resizeListener();
  }, [otherOptionBoxContainerRef?.current]);

  useWindowSize({ resizeFunction: resizeListener });

  const handleSaveFieldLabel = (e) => {
    e.preventDefault();

    if (labelValue.trim() === '') return;
    if (originalFieldLabel === labelValue) return;

    handleOtherFieldLabel(labelValue);
    setOtherFieldLabelEditable(false);
  };

  const handleCloseFieldLabel = (e) => {
    e.preventDefault();

    setLabelValue(otherFieldLabel);
    setOtherFieldLabelEditable(false);
  };

  const handleOpenFieldLabel = (e) => {
    e.preventDefault();

    setOtherFieldLabelEditable(true);
  };

  const handleCancelOtherOption = (e) => {
    e.preventDefault();

    setLabelValue(DEFAULT_OTHER_FIELD_LABEL);
    handleOtherFieldLabel(DEFAULT_OTHER_FIELD_LABEL);
    handleOtherOptionClick(false);
  };

  const handleUpdateLabelValue = (newValue) => {
    setLabelValue(newValue.trim());
  };

  return (
    <OtherOptionBoxContainer ref={otherOptionBoxContainerRef}>
      {otherFieldLabelEditable ? (
        <EditableReasonWrapper className={clsx({ active: otherFieldLabelEditable })}>
          <ContentDisplayWrapper width={detailsWidth}>
            <ContentEditableInput
              style={{ width: detailsWidth, whiteSpace: 'nowrap', color: colors.black.base, padding: '0 2px' }}
              defaultValue={otherFieldLabel}
              id="other-option-label"
              onChange={handleUpdateLabelValue}
            />
          </ContentDisplayWrapper>
          <IconsContainer>
            <StyledIcon
              className={clsx({ disabled: originalFieldLabel === labelValue || labelValue.trim() === '' })}
              onClick={handleSaveFieldLabel}
              aria-hidden="true"
              {...getTestAttributes('save-other-option-button')}
            >
              <Icon height={24} icon={icons.baselineCheck} width={24} />
            </StyledIcon>
            <StyledIcon
              onClick={handleCloseFieldLabel}
              aria-hidden="true"
              {...getTestAttributes('cancel-other-option-button')}
            >
              <Icon height={24} icon={icons.baselineClose} width={24} />
            </StyledIcon>
          </IconsContainer>
        </EditableReasonWrapper>
      ) : (
        <EditableReasonWrapper>
          <ContentDisplayWrapper
            className={clsx({ withPadding: true })}
            width={detailsWidth}
            {...getTestAttributes('other-field-label-value')}
          >
            {labelValue}
          </ContentDisplayWrapper>
          <IconsContainer>
            <StyledIcon
              onClick={handleOpenFieldLabel}
              aria-hidden="true"
              {...getTestAttributes('edit-other-option-button')}
            >
              <Icon height={24} icon={icons.baselineEdit} width={24} />
            </StyledIcon>
            <StyledIcon onClick={handleCancelOtherOption} {...getTestAttributes('cancel-other-option')}>
              <Icon height={24} icon={baselineCancel} width={24} />
            </StyledIcon>
          </IconsContainer>
        </EditableReasonWrapper>
      )}
    </OtherOptionBoxContainer>
  );
}

OtherOptionBox.propTypes = {
  handleOtherOptionClick: PropTypes.func,
  handleOtherFieldLabel: PropTypes.func,
  otherFieldLabel: PropTypes.string,
};

const OtherOptionBoxContainer = styled.div`
  padding: ${sizing.lsmall};
`;

const EditableReasonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  height: ${sizing.xllarge};
  padding: ${sizing.medium};
  font-size: 0.8125rem;
  color: ${colors.black.base};
  border: 1px solid ${colors.grey.quartenary};
  border-radius: ${sizing.xsmall};
  transition: border-color ease-in-out 0.2s, box-shadow ease-in-out 0.2s;
  position: relative;
  background-color: #eaeaea;

  &.active {
    background-color: ${colors.white.primary};
  }
`;

const ContentDisplayWrapper = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;
  width: ${(props) => props.width}px;

  &.with-padding {
    padding: 0 2px;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  gap: ${sizing.medium};
`;

const StyledIcon = styled.div`
  cursor: pointer;
  display: flex;

  &.disabled {
    cursor: not-allowed;
    color: ${colors.grey.quartenary};
  }
`;

export default OtherOptionBox;
